/**
 * useLocale Hook
 *
 * Main hook combining locale context with formatters.
 * Provides a unified API for translations and formatting.
 */

import { useContext } from 'react';

import { useTranslation } from './useTranslation';

import { LocaleContext, NativeLocaleContext } from '../provider';
import {
  formatCompact,
  formatCurrency,
  formatCurrencyCompact,
  formatDate,
  formatDateTime,
  formatFileSize,
  formatLanguageName,
  formatNumber,
  formatPercent,
  formatList,
  formatRelativeTime,
  formatTime,
  formatTimeAgo,
  formatTimeZoneName,
  type DateFormat,
} from '../formatters';
import { LOCALE_MAP, type SupportedLocale } from '../types';
import { formatIQultureScore, formatIQultureScoreParts } from '@tonnie/heroes';

// ═══════════════════════════════════════════════════════════════════════════
// HOOK RETURN TYPE
// ═══════════════════════════════════════════════════════════════════════════

export interface UseLocaleReturn {
  // Translation
  /** Translation function */
  t: ReturnType<typeof useTranslation>['t'];
  /** i18n instance */
  i18n: ReturnType<typeof useTranslation>['i18n'];

  // Locale state
  /** Current locale */
  locale: SupportedLocale;
  /** Intl locale string (e.g., 'en-US') */
  intlLocale: string;
  /** Whether locale is loading */
  isLoading: boolean;
  /** Whether locale is initialized */
  isInitialized: boolean;
  /** Change locale function */
  setLocale: (locale: SupportedLocale) => Promise<void>;

  // Date formatters — in the APP locale. A named style, or explicit Intl
  // options for a shape the styles do not cover (month + year, weekday, …).
  /** Format date (default `'medium'`) */
  formatDate: (date: Date | string | number, format?: DateFormat) => string;
  /** Format date and time (default `'medium'`) */
  formatDateTime: (date: Date | string | number, format?: DateFormat) => string;
  /** Format time only (default `'short'`) */
  formatTime: (date: Date | string | number, format?: DateFormat) => string;
  /** A time zone's name for people ("Brasília Time"), not its IANA id */
  formatTimeZoneName: (timeZone: string) => string;
  /** A language's name for people ("Spanish"), from its ISO 639-1 code */
  formatLanguageName: (code: string) => string;

  // Number formatters
  /** Format number (Intl options optional, e.g. `{ maximumFractionDigits: 0 }`) */
  formatNumber: (value: number, options?: Intl.NumberFormatOptions) => string;
  /** Format percentage */
  formatPercent: (value: number | null | undefined) => string;
  /** Format file size */
  formatFileSize: (bytes: number) => string;
  /** Format compact number */
  formatCompact: (value: number) => string;
  /**
   * An iQulture score (0–100) the one way every surface shows it: one decimal,
   * `.0` trimmed, the reader's separator, no unit, `—` when absent.
   */
  formatScore: (score: number | null | undefined) => string;
  /** The same score split for a two-weight layout (headline digits + small-print decimals). */
  formatScoreParts: (score: number | null | undefined) => { integer: string; fraction: string };

  // Currency formatters
  /** Format currency */
  formatCurrency: (amount: number, currency?: string) => string;
  /** Format compact currency */
  formatCurrencyCompact: (amount: number, currency?: string) => string;

  // Relative time formatters
  /** Format relative time; `style: 'short'` for dense surfaces */
  formatRelativeTime: (
    date: Date | string | number,
    style?: Intl.RelativeTimeFormatStyle
  ) => string;
  /** Format time ago */
  formatTimeAgo: (date: Date | string | number) => string;

  /** Names in a sentence: "A and B", "A, B and C" — the same on the web and the phone. */
  formatList: (items: readonly string[]) => string;
}

/** The formatter half of `useLocale()` — every one bound to ONE Intl locale. */
export type LocaleFormatters = Pick<
  UseLocaleReturn,
  | 'formatDate'
  | 'formatDateTime'
  | 'formatTime'
  | 'formatTimeZoneName'
  | 'formatLanguageName'
  | 'formatNumber'
  | 'formatPercent'
  | 'formatFileSize'
  | 'formatCompact'
  | 'formatScore'
  | 'formatScoreParts'
  | 'formatCurrency'
  | 'formatCurrencyCompact'
  | 'formatRelativeTime'
  | 'formatTimeAgo'
>;

/**
 * The shared formatters bound to `intlLocale` — what `useLocale()` returns for
 * the app locale. Exported so code that renders outside a locale provider (a
 * unit test rendering one component) uses the SAME formatters rather than a
 * hand-rolled stand-in. (F34 · F49)
 */
export function createLocaleFormatters(intlLocale: string): LocaleFormatters {
  return {
    // Date formatters
    formatDate: (date, format) => formatDate(date, intlLocale, format),
    formatDateTime: (date, format) => formatDateTime(date, intlLocale, format),
    formatTime: (date, format) => formatTime(date, intlLocale, format),
    formatTimeZoneName: (timeZone) => formatTimeZoneName(timeZone, intlLocale),
    formatLanguageName: (code) => formatLanguageName(code, intlLocale),

    // Number formatters
    formatNumber: (value, options) => formatNumber(value, intlLocale, options),
    formatPercent: (value) => formatPercent(value, intlLocale),
    formatFileSize: (bytes) => formatFileSize(bytes, intlLocale),
    formatCompact: (value) => formatCompact(value, intlLocale),
    formatScore: (score) => formatIQultureScore(score, intlLocale),
    formatScoreParts: (score) => formatIQultureScoreParts(score, intlLocale),

    // Currency formatters
    formatCurrency: (amount, currency) => formatCurrency(amount, intlLocale, currency),
    formatCurrencyCompact: (amount, currency) =>
      formatCurrencyCompact(amount, intlLocale, currency),

    // Relative time formatters
    formatRelativeTime: (date, style) => formatRelativeTime(date, intlLocale, new Date(), style),
    formatTimeAgo: (date) => formatTimeAgo(date, intlLocale),
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// HOOK
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Main locale hook combining translations and formatters
 *
 * @example
 * ```tsx
 * const { t, locale, setLocale, formatDate, formatCurrency } = useLocale();
 *
 * return (
 *   <div>
 *     <p>{t('common.welcome')}</p>
 *     <p>{formatDate(new Date())}</p>
 *     <p>{formatCurrency(99.99)}</p>
 *   </div>
 * );
 * ```
 */
export function useLocale(): UseLocaleReturn {
  const { t, i18n } = useTranslation();
  // Provider-agnostic: works under the web `LocaleProvider` (LocaleContext) OR
  // the React Native `NativeLocaleProvider` (NativeLocaleContext). Both expose
  // the same `LocaleContextType`, so read whichever is mounted. (useTranslation
  // talks to react-i18next directly and is unaffected by which provider is used —
  // this hook is the only one bound to a locale context, which is why mobile
  // screens calling `useLocale()` crashed under NativeLocaleProvider.)
  const webContext = useContext(LocaleContext);
  const nativeContext = useContext(NativeLocaleContext);
  const localeContext = webContext ?? nativeContext;
  if (!localeContext) {
    throw new Error('useLocale must be used within a LocaleProvider or NativeLocaleProvider');
  }
  const { locale, isLoading, isInitialized, setLocale } = localeContext;

  // Get Intl locale string
  const intlLocale = LOCALE_MAP[locale] || locale;

  return {
    // Translation
    t,
    i18n,

    // Locale state
    locale,
    intlLocale,
    isLoading,
    isInitialized,
    setLocale,

    // Formatters bound to the current locale
    ...createLocaleFormatters(intlLocale),
    formatList: (items) => formatList(items, t),
  };
}

export default useLocale;
